import { Link } from "react-router-dom";
import { MessageCircle, ArrowRight, Target, Eye, Heart, Sparkles, Compass, GraduationCap } from "lucide-react";
import { ABOUT, BRAND, WA_LINK, HERO_IMAGE } from "../lib/constants";
import { SectionHeader } from "../components/SectionHeader";
import { Button } from "../components/ui/button";

const VALUE_ICONS = [Target, Heart, Compass, GraduationCap];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="TASSEI team" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A192F]/95 via-[#0A192F]/80 to-[#00B37E]/40" />
          <div className="absolute inset-0 tassei-dot-bg opacity-25 mix-blend-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 text-white">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-white/20 bg-white/10 backdrop-blur text-[#7CF0BD] text-xs font-bold uppercase tracking-[0.22em]">
            <Sparkles className="w-3.5 h-3.5" /> About TASSEI
          </div>
          <h1 className="mt-5 font-heading font-extrabold text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight max-w-4xl">
            More than a coaching brand — <span className="text-[#7CF0BD]">an ecosystem.</span>
          </h1>
          <p className="mt-6 text-lg text-white/85 leading-relaxed max-w-2xl">
            {ABOUT.story}
          </p>
        </div>
      </section>

      {/* MEANING OF TASSEI */}
      <section className="py-20 md:py-28" data-testid="about-meaning-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
          {/* Kanji block */}
          <div className="lg:col-span-5">
            <div className="relative aspect-square max-w-md rounded-3xl bg-gradient-to-br from-[#0A192F] via-[#0F2648] to-[#00B37E] p-10 flex flex-col items-center justify-center text-white overflow-hidden">
              <div className="absolute inset-0 tassei-dot-bg opacity-20" />
              <div className="relative text-center">
                <div className="text-[10px] uppercase tracking-[0.32em] text-[#7CF0BD] font-bold">Japanese</div>
                <div
                  className="font-heading font-extrabold text-[160px] sm:text-[200px] leading-none mt-2 tracking-tight"
                  data-testid="kanji-display"
                  style={{ fontFamily: '"Noto Serif JP", serif' }}
                >
                  {ABOUT.kanji}
                </div>
                <div className="font-heading text-2xl font-bold mt-4">{ABOUT.romaji}</div>
                <div className="text-xs uppercase tracking-[0.22em] text-white/80 mt-2">
                  {ABOUT.jpMeaning}
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <SectionHeader
              eyebrow="The Meaning"
              title="Why we're called TASSEI."
            />
            <p className="mt-5 text-lg text-[#475569] leading-relaxed">
              {ABOUT.jpDescription}
            </p>
            <p className="mt-4 text-base text-[#475569] leading-relaxed">
              For us, &ldquo;TASSEI&rdquo; is more than a name — it&apos;s a promise. It&apos;s also a perfect acronym for what we do in India:
            </p>

            <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="acronym-grid">
              {ABOUT.acronym.map((a, i) => (
                <div
                  key={i}
                  data-testid={`acronym-${i}`}
                  className="bg-white border border-slate-200 rounded-xl p-4 hover:border-[#00B37E] hover:-translate-y-0.5 transition-all"
                >
                  <div className="font-heading text-3xl font-extrabold text-[#00B37E] leading-none">
                    {a.letter}
                  </div>
                  <div className="text-sm font-bold text-[#0A192F] mt-2">{a.word}</div>
                </div>
              ))}
            </div>

            <div className="mt-7 inline-flex items-center gap-2 text-sm text-[#64748B]">
              <span className="font-bold text-[#0A192F]">Tagline:</span> {BRAND.tagline} — From Dreams to Real Success.
            </div>
          </div>
        </div>
      </section>

      {/* MISSION + VISION */}
      <section className="py-20 md:py-24 bg-[#0A192F] text-white relative overflow-hidden" data-testid="about-mission-vision">
        <div className="absolute inset-0 tassei-dot-bg opacity-30" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 grid md:grid-cols-2 gap-6">
          <div className="bg-white/[0.04] backdrop-blur border border-white/10 rounded-3xl p-8 md:p-10">
            <div className="w-12 h-12 rounded-xl bg-[#00B37E] flex items-center justify-center">
              <Target className="w-6 h-6 text-white" />
            </div>
            <div className="mt-5 text-xs uppercase tracking-[0.22em] font-bold text-[#7CF0BD]">Our Mission</div>
            <h2 className="mt-3 font-heading text-3xl font-extrabold leading-tight">
              {ABOUT.mission}
            </h2>
          </div>
          <div className="bg-white/[0.04] backdrop-blur border border-white/10 rounded-3xl p-8 md:p-10">
            <div className="w-12 h-12 rounded-xl bg-[#7CF0BD] flex items-center justify-center">
              <Eye className="w-6 h-6 text-[#0A192F]" />
            </div>
            <div className="mt-5 text-xs uppercase tracking-[0.22em] font-bold text-[#7CF0BD]">Our Vision</div>
            <h2 className="mt-3 font-heading text-3xl font-extrabold leading-tight">
              {ABOUT.vision}
            </h2>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-20 md:py-28" data-testid="about-values-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <SectionHeader
            eyebrow="Our Values"
            title="What we stand for."
            desc="Four non-negotiables that shape every course, conversation and counselling session at TASSEI."
          />

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {ABOUT.values.map((v, idx) => {
              const Icon = VALUE_ICONS[idx % VALUE_ICONS.length];
              return (
                <div
                  key={idx}
                  data-testid={`value-${idx}`}
                  className="bg-white border border-slate-200 rounded-2xl p-6 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#0A192F]/5 transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#00B37E]/12 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-[#00B37E]" />
                  </div>
                  <h3 className="mt-5 font-heading text-lg font-bold text-[#0A192F]">{v.title}</h3>
                  <p className="mt-2 text-sm text-[#64748B] leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24" data-testid="about-cta-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#00B37E] to-[#0A192F] p-10 md:p-14 text-white">
            <div className="absolute inset-0 opacity-20 tassei-dot-bg" />
            <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="max-w-2xl">
                <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#7CF0BD]">Join the ecosystem</div>
                <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold leading-tight">
                  Let&apos;s map your path from dream to safalta.
                </h2>
                <p className="mt-4 text-white/85 text-base">
                  Talk to our counsellor for free — we&apos;ll guide you to the right course, university or career step.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={WA_LINK("Hi TASSEI, I want a free consultation")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-cta-whatsapp"
                >
                  <Button className="bg-white hover:bg-slate-100 text-[#0A192F] font-bold rounded-full h-12 px-7">
                    <MessageCircle className="w-5 h-5 mr-2" /> Free Consultation
                  </Button>
                </a>
                <Link to="/courses" data-testid="about-cta-courses">
                  <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#0A192F] font-semibold rounded-full h-12 px-7 bg-transparent">
                    Explore Courses <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
